/**
 * Proforma Summary Builders
 * =========================
 *
 * This module provides deterministic summary-building functions for the "condensed"
 * view of the Triage Proforma. Instead of displaying every question as its own line
 * item, the condensed view groups questions by their Category of Information (CoI)
 * and produces a single text-box summary for each CoI.
 *
 * Architecture:
 *   - SUMMARY_BUILDERS is a registry keyed by formKey → categoryOfInformation → builder fn.
 *   - Each builder receives the array of questions in that CoI group plus the full
 *     answers object, and returns a formatted multi-line string.
 *   - CoIs without a registered builder fall back to single-question display using
 *     the existing getDisplayValue() function in the rendering component.
 *
 * This file is extended one form at a time as we convert each proforma stage to
 * the condensed layout. Currently covers: theirEarlyLife (Developmental History).
 *
 * The approach is analogous to PrefillFunctions.js in the ADHD assessment app,
 * where each question has a custom function that deterministically builds the
 * content for its text box — but here the output is a read-only summary string
 * rather than a prefilled form value.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Answer Resolution Helpers
// These convert raw answer data (optionIds, milestone objects, etc.) into
// human-readable strings. They mirror the logic in getDisplayValue() from
// RmsTriageProforma.js but are designed for use within summary builders.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Resolves a radio/select answer to its display label by looking up the
 * optionId in the question's options array. Handles both the simple string
 * format ("yes") and the object format ({ optionId: "yes", otherText: "..." }).
 */
function resolveRadioAnswer(question, answer) {
  if (answer === null || answer === undefined || answer === '') return 'No answer given';

  const options = question?.options || [];

  if (typeof answer === 'string') {
    const opt = options.find((o) => o.optionId === answer);
    return opt?.label || answer;
  }

  if (typeof answer === 'object' && answer.optionId) {
    const opt = options.find((o) => o.optionId === answer.optionId);
    let label = opt?.label || answer.optionId;
    if (answer.otherText) label += ` - _"${answer.otherText}"_`;
    return label;
  }

  return String(answer);
}

/**
 * Resolves a checkboxes answer to an array of display labels.
 * Each element in the answer array can be a plain string (optionId) or
 * an object ({ optionId, otherText }).
 */
function resolveCheckboxLabels(question, answer) {
  if (!Array.isArray(answer) || answer.length === 0) return [];

  const options = question?.options || [];
  return answer.map((a) => {
    const id = typeof a === 'string' ? a : a.optionId;
    const opt = options.find((o) => o.optionId === id);
    let label = opt?.label || id;
    if (typeof a === 'object' && a.otherText) label += ` - _"${a.otherText}"_`;
    return label;
  });
}

/**
 * Resolves a milestoneAgeGroup answer into an array of "Label: Value" strings.
 * Uses the milestone definitions on the question to map milestoneIds to labels,
 * and formats ages with abbreviated units (3mo, 2yr, etc.).
 */
function resolveMilestoneLines(question, answer) {
  if (!answer || typeof answer !== 'object') return [];

  const milestones = question?.milestones || [];
  return milestones.map((m) => {
    const val = answer[m.milestoneId];
    if (!val) return `${m.label}: Not specified`;
    if (val.notYetDoing) return `${m.label}: Not yet doing`;
    if (val.notSure) return `${m.label}: Not sure`;
    if (val.age == null) return `${m.label}: Not specified`;
    const unitAbbrev = val.unit === 'years' ? 'yr' : 'mo';
    return `${m.label}: ${val.age}${unitAbbrev}`;
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// theirEarlyLife (Developmental History) Builders
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Developmental milestones — combines 4 questions into one summary:
 *   1. developmental_milestones (milestoneAgeGroup) → bullet list of milestone ages
 *   2. toilet_training_difficulties (radio) → "Difficulties with toilet training: ..."
 *   3. slower_to_reach_skills (radio) → "Health professional has said ... slow to reach ..."
 *   4. lost_skills (radio) → "Noticed that they had lost skills ..."
 *
 * The milestone ages form the main body as a bullet list, followed by the three
 * supplementary radio answers as single-line summaries separated by blank lines.
 */
function buildDevelopmentalMilestonesSummary(questions, answers) {
  const parts = [];

  const milestonesQ = questions.find((q) => q.questionId === 'developmental_milestones');
  const toiletQ = questions.find((q) => q.questionId === 'toilet_training_difficulties');
  const slowerQ = questions.find((q) => q.questionId === 'slower_to_reach_skills');
  const lostQ = questions.find((q) => q.questionId === 'lost_skills');

  // Milestone ages as a bullet list (the primary body of this CoI)
  if (milestonesQ) {
    const milestoneLines = resolveMilestoneLines(milestonesQ, answers[milestonesQ.questionId]);
    parts.push('Milestones reached at:');
    parts.push(...milestoneLines.map((l) => `• ${l}`));
  }

  // Toilet training difficulties — rewording removes the parenthetical examples
  if (toiletQ) {
    const ans = resolveRadioAnswer(toiletQ, answers[toiletQ.questionId]);
    parts.push('');
    parts.push(`Difficulties with toilet training: ${ans}`);
  }

  // Slower to reach milestones — condenses the verbose original question
  if (slowerQ) {
    const ans = resolveRadioAnswer(slowerQ, answers[slowerQ.questionId]);
    parts.push('');
    parts.push(`Health professional has said that they were slow to reach any milestone: ${ans}`);
  }

  // Lost previously-developed skills — condenses the verbose original question
  if (lostQ) {
    const ans = resolveRadioAnswer(lostQ, answers[lostQ.questionId]);
    parts.push('');
    parts.push(`Noticed that they had lost skills previously developed: ${ans}`);
  }

  return parts.join('\n');
}

/**
 * Monthly cycles — combines 2 questions into one summary:
 *   1. started_periods (radio) → "Started periods: ..."
 *   2. difficulties_around_period (radio, conditional) → "Difficulties worsen around period: ..."
 *
 * The second question is only shown to the user when started_periods = "yes",
 * so we only include it in the summary when an answer exists.
 */
function buildMonthlyCyclesSummary(questions, answers) {
  const parts = [];

  const periodsQ = questions.find((q) => q.questionId === 'started_periods');
  const difficultiesQ = questions.find((q) => q.questionId === 'difficulties_around_period');

  if (periodsQ) {
    const ans = resolveRadioAnswer(periodsQ, answers[periodsQ.questionId]);
    parts.push(`Started periods: ${ans}`);
  }

  // Only include the follow-up if the respondent actually answered it
  // (it's conditionally visible — only shown when started_periods = "yes")
  if (difficultiesQ && answers[difficultiesQ.questionId] != null) {
    const ans = resolveRadioAnswer(difficultiesQ, answers[difficultiesQ.questionId]);
    parts.push('');
    parts.push(`Difficulties worsen around period: ${ans}`);
  }

  return parts.join('\n');
}

/**
 * ACEs (Adverse Childhood Experiences) — combines 2 questions into one summary:
 *   1. aces (checkboxes) → bullet list of selected experiences
 *   2. scary_upsetting_event (radio) → "Experienced a very scary or upsetting event: ..."
 */
function buildACEsSummary(questions, answers) {
  const parts = [];

  const acesQ = questions.find((q) => q.questionId === 'aces');
  const scaryQ = questions.find((q) => q.questionId === 'scary_upsetting_event');

  // ACE items as a bullet list
  if (acesQ) {
    const labels = resolveCheckboxLabels(acesQ, answers[acesQ.questionId]);
    if (labels.length > 0) {
      parts.push('ACEs experienced:');
      parts.push(...labels.map((l) => `• ${l}`));
    } else {
      parts.push('No answer given');
    }
  }

  // Scary/upsetting event follow-up
  if (scaryQ) {
    const ans = resolveRadioAnswer(scaryQ, answers[scaryQ.questionId]);
    parts.push('');
    parts.push(`Experienced a very scary or upsetting event: ${ans}`);
  }

  return parts.join('\n');
}

// ─────────────────────────────────────────────────────────────────────────────
// theirFamilyAndHomeLife (Family and Home Life) Builders
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Structure at Home — combines 2 questions into one summary:
 *   1. who_lives_at_home (checkboxes) → bullet list of household members
 *   2. family_not_living_together (radio) → "Family members not living with them: ..."
 */
function buildStructureAtHomeSummary(questions, answers) {
  const parts = [];

  const whoLivesQ = questions.find((q) => q.questionId === 'who_lives_at_home');
  const notLivingQ = questions.find((q) => q.questionId === 'family_not_living_together');

  if (whoLivesQ) {
    const labels = resolveCheckboxLabels(whoLivesQ, answers[whoLivesQ.questionId]);
    if (labels.length > 0) {
      parts.push('Lives with:');
      parts.push(...labels.map((l) => `• ${l}`));
    } else {
      parts.push('Lives with: No answer given');
    }
  }

  if (notLivingQ) {
    const ans = resolveRadioAnswer(notLivingQ, answers[notLivingQ.questionId]);
    parts.push('');
    parts.push(`Family members not living with them: ${ans}`);
  }

  return parts.join('\n');
}

/**
 * Effectiveness of Strategies at Home — combines 2 questions into one summary:
 *   1. helpful_strategies → "Strategies that help: ..." — multi-select
 *      (checkboxes) since schema v1.1.0; instances pinned to v1.0.0 still hold
 *      a single radio answer, so both shapes must resolve.
 *   2. unhelpful_strategies (radio) → "Strategies tried that haven't worked: ..."
 */
function buildStrategiesAtHomeSummary(questions, answers) {
  const parts = [];

  const helpfulQ = questions.find((q) => q.questionId === 'helpful_strategies');
  const unhelpfulQ = questions.find((q) => q.questionId === 'unhelpful_strategies');

  if (helpfulQ) {
    const helpfulAnswer = answers[helpfulQ.questionId];
    if (Array.isArray(helpfulAnswer)) {
      const labels = resolveCheckboxLabels(helpfulQ, helpfulAnswer);
      if (labels.length > 0) {
        parts.push('Strategies that help:');
        parts.push(...labels.map((l) => `• ${l}`));
      } else {
        parts.push('Strategies that help: No answer given');
      }
    } else {
      const ans = resolveRadioAnswer(helpfulQ, helpfulAnswer);
      parts.push(`Strategies that help: ${ans}`);
    }
  }

  if (unhelpfulQ) {
    const ans = resolveRadioAnswer(unhelpfulQ, answers[unhelpfulQ.questionId]);
    parts.push('');
    parts.push(`Strategies tried that haven't worked: ${ans}`);
  }

  return parts.join('\n');
}

/**
 * Parental / Carer Support — combines 2 questions into one summary:
 *   1. parental_coping (radio) → "Currently coping: ..."
 *   2. support_used (checkboxes) → bullet list of support sources used
 */
function buildParentalCarerSupportSummary(questions, answers) {
  const parts = [];

  const copingQ = questions.find((q) => q.questionId === 'parental_coping');
  const supportQ = questions.find((q) => q.questionId === 'support_used');

  if (copingQ) {
    const ans = resolveRadioAnswer(copingQ, answers[copingQ.questionId]);
    parts.push(`Currently coping: ${ans}`);
  }

  if (supportQ) {
    const labels = resolveCheckboxLabels(supportQ, answers[supportQ.questionId]);
    parts.push('');
    if (labels.length > 0) {
      parts.push('Support used:');
      parts.push(...labels.map((l) => `• ${l}`));
    } else {
      parts.push('Support used: No answer given');
    }
  }

  return parts.join('\n');
}

// ─────────────────────────────────────────────────────────────────────────────
// theirLifestyle (Lifestyle History) Builders
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Frequency of physical activity — combines 2 questions into one summary:
 *   1. daily_movement (radio) → "At least 1 hour of movement daily: ..."
 *   2. strength_coordination (radio) → "Strength/coordination activities 3 times per week: ..."
 */
function buildPhysicalActivityFrequencySummary(questions, answers) {
  const parts = [];

  const movementQ = questions.find((q) => q.questionId === 'daily_movement');
  const strengthQ = questions.find((q) => q.questionId === 'strength_coordination');

  if (movementQ) {
    const ans = resolveRadioAnswer(movementQ, answers[movementQ.questionId]);
    parts.push(`At least 1 hour of movement daily: ${ans}`);
  }

  if (strengthQ) {
    const ans = resolveRadioAnswer(strengthQ, answers[strengthQ.questionId]);
    parts.push('');
    parts.push(`Strength/coordination activities 3 times per week: ${ans}`);
  }

  return parts.join('\n');
}

/**
 * Caffeine Use — combines 3 questions into one summary:
 *   1. caffeine_use (radio) → "Consumes caffeinated drinks: ..."
 *   2. caffeine_amount (radio, conditional) → "Caffeinated drinks per day: ..."
 *   3. caffeine_impact (radio, conditional) → "Impact on sleep, mood, or behaviour: ..."
 *
 * Questions 2 and 3 are conditionally visible (only when caffeine_use = "yes"),
 * so they are only included when the respondent actually answered them.
 */
function buildCaffeineUseSummary(questions, answers) {
  const parts = [];

  const useQ = questions.find((q) => q.questionId === 'caffeine_use');
  const amountQ = questions.find((q) => q.questionId === 'caffeine_amount');
  const impactQ = questions.find((q) => q.questionId === 'caffeine_impact');

  if (useQ) {
    const ans = resolveRadioAnswer(useQ, answers[useQ.questionId]);
    parts.push(`Consumes caffeinated drinks: ${ans}`);
  }

  if (amountQ && answers[amountQ.questionId] != null) {
    const ans = resolveRadioAnswer(amountQ, answers[amountQ.questionId]);
    parts.push('');
    parts.push(`Caffeinated drinks per day: ${ans}`);
  }

  if (impactQ && answers[impactQ.questionId] != null) {
    const ans = resolveRadioAnswer(impactQ, answers[impactQ.questionId]);
    parts.push('');
    parts.push(`Impact on sleep, mood, or behaviour: ${ans}`);
  }

  return parts.join('\n');
}

/**
 * Friendships — combines 4 questions into one summary:
 *   1. prefer_time_with (checkboxes) → "Prefers to spend time with:" + bullet list
 *   2. has_friends (checkboxes) → single-line summary of friendship status
 *   3. friendship_nature (radio) → "Nature of friendships: ..."
 *   4. online_friendships (radio) → "Online friendships: ..."
 */
function buildFriendshipsSummary(questions, answers) {
  const parts = [];

  const preferQ = questions.find((q) => q.questionId === 'prefer_time_with');
  const hasFriendsQ = questions.find((q) => q.questionId === 'has_friends');
  const natureQ = questions.find((q) => q.questionId === 'friendship_nature');
  const onlineQ = questions.find((q) => q.questionId === 'online_friendships');

  if (preferQ) {
    const labels = resolveCheckboxLabels(preferQ, answers[preferQ.questionId]);
    if (labels.length > 0) {
      parts.push('Prefers to spend time with:');
      parts.push(...labels.map((l) => `• ${l}`));
    } else {
      parts.push('Prefers to spend time with: No answer given');
    }
  }

  if (hasFriendsQ) {
    const labels = resolveCheckboxLabels(hasFriendsQ, answers[hasFriendsQ.questionId]);
    parts.push('');
    if (labels.length > 0) {
      parts.push('Has friends:');
      parts.push(...labels.map((l) => `• ${l}`));
    } else {
      parts.push('Has friends: No answer given');
    }
  }

  if (natureQ) {
    const ans = resolveRadioAnswer(natureQ, answers[natureQ.questionId]);
    parts.push('');
    parts.push(`Nature of friendships: ${ans}`);
  }

  if (onlineQ) {
    const ans = resolveRadioAnswer(onlineQ, answers[onlineQ.questionId]);
    parts.push('');
    parts.push(`Online friendships: ${ans}`);
  }

  return parts.join('\n');
}

/**
 * Conflict — combines 2 questions into one summary:
 *   1. conflict_frequency (radio) → "Falls out or argues with other children: ..."
 *   2. conflict_coping (radio) → "Coping with friendship problems: ..."
 */
function buildConflictSummary(questions, answers) {
  const parts = [];

  const frequencyQ = questions.find((q) => q.questionId === 'conflict_frequency');
  const copingQ = questions.find((q) => q.questionId === 'conflict_coping');

  if (frequencyQ) {
    const ans = resolveRadioAnswer(frequencyQ, answers[frequencyQ.questionId]);
    parts.push(`Falls out or argues with other children: ${ans}`);
  }

  if (copingQ) {
    const ans = resolveRadioAnswer(copingQ, answers[copingQ.questionId]);
    parts.push('');
    parts.push(`Coping with friendship problems: ${ans}`);
  }

  return parts.join('\n');
}

/**
 * Social vulnerability — combines 2 questions into one summary:
 *   1. social_vulnerability (checkboxes) → "Concerns:" + bullet list
 *   2. bullied_others (radio) → "Has bullied others: ..."
 */
function buildSocialVulnerabilitySummary(questions, answers) {
  const parts = [];

  const vulnQ = questions.find((q) => q.questionId === 'social_vulnerability');
  const bulliedQ = questions.find((q) => q.questionId === 'bullied_others');

  if (vulnQ) {
    const labels = resolveCheckboxLabels(vulnQ, answers[vulnQ.questionId]);
    if (labels.length > 0) {
      parts.push('Concerns:');
      parts.push(...labels.map((l) => `• ${l}`));
    } else {
      parts.push('Concerns: No answer given');
    }
  }

  if (bulliedQ) {
    const ans = resolveRadioAnswer(bulliedQ, answers[bulliedQ.questionId]);
    parts.push('');
    parts.push(`Has bullied others: ${ans}`);
  }

  return parts.join('\n');
}

// ─────────────────────────────────────────────────────────────────────────────
// theirHistoryWithTheLaw (Forensic History) Builders
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Offending Behaviour — combines 4 questions into one summary:
 *   1. offending_behaviours (checkboxes) → "Behaviours engaged in:" + bullet list
 *   2. behaviour_frequency (radio, conditional) → "How often: ..."
 *   3. behaviour_nature (radio, conditional) → "Nature of behaviours: ..."
 *   4. online_behaviour_concerns (checkboxes) → "Online behaviour concerns:" + bullet list
 *
 * Questions 2 and 3 are conditionally visible (only when offending behaviours
 * other than "none"/"not sure"/"prefer not to say" are selected), so they are
 * only included in the summary when an answer exists.
 */
function buildOffendingBehaviourSummary(questions, answers) {
  const parts = [];

  const behavioursQ = questions.find((q) => q.questionId === 'offending_behaviours');
  const frequencyQ = questions.find((q) => q.questionId === 'behaviour_frequency');
  const natureQ = questions.find((q) => q.questionId === 'behaviour_nature');
  const onlineQ = questions.find((q) => q.questionId === 'online_behaviour_concerns');

  if (behavioursQ) {
    const labels = resolveCheckboxLabels(behavioursQ, answers[behavioursQ.questionId]);
    if (labels.length > 0) {
      parts.push('Behaviours engaged in:');
      parts.push(...labels.map((l) => `• ${l}`));
    } else {
      parts.push('Behaviours engaged in: No answer given');
    }
  }

  if (frequencyQ && answers[frequencyQ.questionId] != null) {
    const ans = resolveRadioAnswer(frequencyQ, answers[frequencyQ.questionId]);
    parts.push('');
    parts.push(`How often: ${ans}`);
  }

  if (natureQ && answers[natureQ.questionId] != null) {
    const ans = resolveRadioAnswer(natureQ, answers[natureQ.questionId]);
    parts.push('');
    parts.push(`Nature of behaviours: ${ans}`);
  }

  if (onlineQ) {
    const labels = resolveCheckboxLabels(onlineQ, answers[onlineQ.questionId]);
    parts.push('');
    if (labels.length > 0) {
      parts.push('Online behaviour concerns:');
      parts.push(...labels.map((l) => `• ${l}`));
    } else {
      parts.push('Online behaviour concerns: No answer given');
    }
  }

  return parts.join('\n');
}

// ─────────────────────────────────────────────────────────────────────────────
// theirMedicalHistory (Medical History) Builders
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Vision — combines 2 questions into one summary:
 *   1. last_eye_test (radio) → "Last eye test: ..."
 *   2. wears_glasses (radio) → "Wears prescription glasses regularly: ..."
 */
function buildVisionSummary(questions, answers) {
  const parts = [];

  const eyeTestQ = questions.find((q) => q.questionId === 'last_eye_test');
  const glassesQ = questions.find((q) => q.questionId === 'wears_glasses');

  if (eyeTestQ) {
    const ans = resolveRadioAnswer(eyeTestQ, answers[eyeTestQ.questionId]);
    parts.push(`Last eye test: ${ans}`);
  }

  if (glassesQ) {
    const ans = resolveRadioAnswer(glassesQ, answers[glassesQ.questionId]);
    parts.push('');
    parts.push(`Wears prescription glasses regularly: ${ans}`);
  }

  return parts.join('\n');
}

// ─────────────────────────────────────────────────────────────────────────────
// educationHistory Builders (Dual-respondent — School + Guardian)
// ─────────────────────────────────────────────────────────────────────────────
// Education History is a dual-respondent form: school and guardian are asked
// different questions for many of the same CoIs. Builders here are invoked
// per-respondent — the condensed panel calls them with the specific questions
// and answers belonging to one respondent. A builder is only triggered when
// the respondent has 2+ questions sharing the same CoI; single-question CoIs
// fall through to getDisplayValue() as usual.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Current and previous education environment (guardian) — combines 2 questions:
 *   1. lg_attended_settings (checkboxes) — "Have they ever attended any of the following?"
 *   2. lg_current_education_status (radio) — "What is their current education status?"
 *
 * The school side only has 1 question for this CoI so no builder is needed there.
 */
function buildCurrentEducationEnvironmentSummary(questions, answers) {
  const lines = [];

  const attendedQ = questions.find((q) => q.questionId === 'lg_attended_settings');
  if (attendedQ) {
    const labels = resolveCheckboxLabels(attendedQ, answers[attendedQ.questionId]);
    if (labels.length > 0) {
      lines.push('Settings attended:');
      labels.forEach((l) => lines.push(`• ${l}`));
    }
  }

  const statusQ = questions.find((q) => q.questionId === 'lg_current_education_status');
  if (statusQ) {
    const ans = resolveRadioAnswer(statusQ, answers[statusQ.questionId]);
    lines.push('');
    lines.push(`Current status: ${ans}`);
  }

  return lines.join('\n') || null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Summary Builder Registry
// ─────────────────────────────────────────────────────────────────────────────
// Keyed by formKey → categoryOfInformation → builder function.
// When adding a new form, add a new top-level key here with entries for each
// multi-question CoI that needs a custom summary. Single-question CoIs don't
// need an entry — they fall back to the standard single-question display.
// ─────────────────────────────────────────────────────────────────────────────

const SUMMARY_BUILDERS = {
  theirEarlyLife: {
    'Developmental milestones': buildDevelopmentalMilestonesSummary,
    'Monthly cycles': buildMonthlyCyclesSummary,
    'ACEs': buildACEsSummary,
  },
  theirFamilyAndHomeLife: {
    'Structure at Home': buildStructureAtHomeSummary,
    'Effectiveness of Strategies at Home': buildStrategiesAtHomeSummary,
    'Parental / Carer Support': buildParentalCarerSupportSummary,
  },
  theirLifestyle: {
    'Frequency of physical activity': buildPhysicalActivityFrequencySummary,
    'Caffeine Use': buildCaffeineUseSummary,
    'Friendships': buildFriendshipsSummary,
    'Conflict': buildConflictSummary,
    'Social vulnerability': buildSocialVulnerabilitySummary,
  },
  theirHistoryWithTheLaw: {
    'Offending Behaviour': buildOffendingBehaviourSummary,
  },
  theirMedicalHistory: {
    'Vision': buildVisionSummary,
  },
  educationHistory: {
    'Current and previous education environment': buildCurrentEducationEnvironmentSummary,
  },
};

/**
 * Look up a custom summary builder for a given form and category of information.
 *
 * @param {string} formKey - The form key (e.g. 'theirEarlyLife')
 * @param {string} categoryOfInformation - The CoI label (e.g. 'Developmental milestones')
 * @returns {Function|null} Builder function, or null if no custom builder exists
 *          (caller should fall back to default single-question display)
 */
export function getSummaryBuilder(formKey, categoryOfInformation) {
  return SUMMARY_BUILDERS[formKey]?.[categoryOfInformation] || null;
}

/**
 * Check whether a given form has ANY condensed-view builders registered.
 * Used by the rendering layer to decide whether to offer the condensed toggle
 * for a particular proforma section.
 *
 * @param {string} formKey - The form key (e.g. 'theirEarlyLife')
 * @returns {boolean} True if at least one CoI builder exists for this form
 */
export function hasCondensedBuilders(formKey) {
  return !!SUMMARY_BUILDERS[formKey] && Object.keys(SUMMARY_BUILDERS[formKey]).length > 0;
}
